import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const ClientsCarousel = () => {
  const [isPaused, setIsPaused] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const clients = [
    {
      name: 'LifeCell',
      logo: '/images/logos/lifecell.png',
      alt: 'LifeCell Logo'
    },
    {
      name: 'Femina Miss India',
      logo: '/images/logos/femina-miss-india.png',
      alt: 'Femina Miss India Logo'
    },
    {
      name: 'Mazars',
      logo: '/images/logos/mazars.png',
      alt: 'Mazars Logo'
    },
    {
      name: 'Reliance Jewels',
      logo: '/images/logos/reliance-jewels.png',
      alt: 'Reliance Jewels Logo'
    },
    {
      name: 'PC Jeweller',
      logo: '/images/logos/pc-jeweller.png',
      alt: 'PC Jeweller Logo'
    },
    {
      name: 'Aditya Birla Group',
      logo: '/images/logos/aditya-birla.png',
      alt: 'Aditya Birla Group Logo'
    },
    {
      name: 'Gitanjali',
      logo: '/images/logos/gitanjali.png',
      alt: 'Gitanjali Logo'
    },
    {
      name: 'Mindshare',
      logo: '/images/logos/mindshare.png',
      alt: 'Mindshare Logo'
    },
    {
      name: 'UltraTech Cement',
      logo: '/images/logos/ultratech.png',
      alt: 'UltraTech Cement Logo'
    },
    {
      name: 'Dabur',
      logo: '/images/logos/dabur.png',
      alt: 'Dabur Logo'
    },
    {
      name: 'Lodha',
      logo: '/images/logos/lodha.png',
      alt: 'Lodha Logo'
    },
    {
      name: 'KPMG',
      logo: '/images/logos/kpmg.png',
      alt: 'KPMG Logo'
    },
    {
      name: 'Delhi Capitals',
      logo: '/images/logos/delhi-capitals.png',
      alt: 'Delhi Capitals Logo'
    },
    {
      name: 'Unilever',
      logo: '/images/logos/unilever.png',
      alt: 'Unilever Logo'
    },
    {
      name: 'BCCI',
      logo: '/images/logos/bcci.png',
      alt: 'BCCI Logo'
    },
    {
      name: 'TA Associates',
      logo: '/images/logos/ta-associates.png',
      alt: 'TA Associates Logo'
    },
    {
      name: 'Mumbai Indians',
      logo: '/images/logos/mumbai-indians.png',
      alt: 'Mumbai Indians Logo'
    }
  ];

  const itemsPerView = 6;
  const maxIndex = Math.max(0, clients.length - itemsPerView);

  // Auto-scroll functionality
  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isPaused, maxIndex]);

  const goToPrevious = () => {
    setCurrentIndex(prev => (prev <= 0 ? maxIndex : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
  };

  const getVisibleClients = () => {
    const visible = [];
    for (let i = 0; i < itemsPerView; i++) {
      const index = (currentIndex + i) % clients.length;
      visible.push({ ...clients[index], key: `${clients[index].name}-${currentIndex}-${i}` });
    }
    return visible;
  };

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 font-heading mb-6">
            Our Trusted Partners
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Building success stories with industry leaders and innovative brands worldwide
          </p>
        </div>

        <div className="relative">
          {/* Navigation Arrows */}
          <button
            onClick={goToPrevious}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 p-3 bg-white shadow-lg rounded-full hover:bg-gray-50 transition-colors border border-gray-200"
            aria-label="Previous logos"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>

          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 p-3 bg-white shadow-lg rounded-full hover:bg-gray-50 transition-colors border border-gray-200"
            aria-label="Next logos"
          >
            <ChevronRight className="w-6 h-6 text-gray-600" />
          </button>

          {/* Carousel Container */}
          <div
            className="mx-12 overflow-hidden"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
              {getVisibleClients().map((client) => (
                <div
                  key={client.key}
                  className="flex items-center justify-center h-28 bg-white rounded-lg border border-gray-200 shadow-sm p-4 transition-all duration-300 hover:shadow-md hover:scale-105"
                >
                  <img
                    src={client.logo}
                    alt={client.alt}
                    className="max-h-16 max-w-full w-auto h-auto object-contain transition-all duration-300"
                    style={
                      ['Delhi Capitals', 'Dabur', 'KPMG', 'Lodha'].includes(client.name)
                        ? { maxWidth: '160px', maxHeight: '64px' }
                        : {}
                    }
                    onError={(e) => {
                      console.error(`Failed to load logo: ${client.logo}`);
                      e.target.style.display = 'none';
                      const fallback = e.target.parentNode.querySelector('.fallback-text');
                      if (!fallback) {
                        const textElement = document.createElement('div');
                        textElement.className = 'fallback-text text-sm text-gray-600 font-medium text-center';
                        textElement.textContent = client.name;
                        e.target.parentNode.appendChild(textElement);
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-lg text-gray-500 font-medium">
            Trusted by 100+ brands • 500+ successful campaigns • 25+ years of excellence
          </p>
        </div>
      </div>
    </section>
  );
};